<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { type UploadFileInfo, type UploadCustomRequestOptions } from 'naive-ui';
import { IconCheckCircle, IconUpload } from '@/components/design/icons';
import { useVaultStore } from '@/stores/vaultStore';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaUploadButton from '@/components/design/base/GaiaUploadButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaProgressBar from '@/components/design/base/GaiaProgressBar.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';

const props = defineProps<{
  show: boolean;
  fileUid: string;
  fileName: string;
}>();

const emit = defineEmits<{
  'update:show': [value: boolean];
  uploaded: [];
}>();

const vaultStore = useVaultStore();
const message = useGaiaToast();

const CHUNK_SIZE = 1024 * 1024 * 3; // 3 MB

const description = ref('');
const selectedFile = ref<File | null>(null);
const uploading = ref(false);
const progress = ref(0);
const uploadDone = ref(false);
const errorMsg = ref('');

const canUpload = computed(
  () => Boolean(selectedFile.value) && !uploading.value && !uploadDone.value
);

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

function reset(): void {
  description.value = '';
  selectedFile.value = null;
  uploading.value = false;
  progress.value = 0;
  uploadDone.value = false;
  errorMsg.value = '';
}

watch(
  () => props.show,
  show => {
    if (!show) reset();
  }
);

function handleChange(data: { fileList: UploadFileInfo[] }): void {
  const last = data.fileList.at(-1);
  selectedFile.value = last?.file ?? null;
  errorMsg.value = '';
}

function handleCustomRequest({ onFinish }: UploadCustomRequestOptions): void {
  onFinish();
}

function handleClose(): void {
  if (uploading.value) return;
  emit('update:show', false);
}

async function handleUpload(): Promise<void> {
  if (!selectedFile.value || uploading.value) return;
  uploading.value = true;
  errorMsg.value = '';
  progress.value = 0;

  try {
    const file = selectedFile.value;
    const uploadId = await vaultStore.initializeRevisionUpload(props.fileUid, {
      file_name: file.name,
      total_bytes: file.size,
      change_description: description.value.trim() || null,
    });

    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      await vaultStore.uploadFileChunk(uploadId, chunk, i, totalChunks);
      progress.value = Math.round(((i + 1) / totalChunks) * 100);
    }

    await vaultStore.finalizeRevisionUpload(props.fileUid, uploadId);
    uploadDone.value = true;
    message.success('New version uploaded successfully.');

    setTimeout(() => {
      emit('uploaded');
      emit('update:show', false);
    }, 900);
  } catch (error) {
    errorMsg.value = error instanceof Error ? error.message : 'Upload failed. Please try again.';
    uploading.value = false;
  }
}

const successColor = gaiaStatus.success;
// Bound from the template (an icon colour), so it stays a plain const.
const subtleColor = gaiaNeutral.textSubtle;

/**
 * Inline custom properties on the modal card rather than `v-bind()`. This
 * component's root is a Teleport, so Vue has no root element in place to
 * write `v-bind()` vars onto and every colour bound that way resolved to
 * nothing. On the card they cascade to header, body and footer alike.
 */
const styleVars = {
  '--revup-accent-color': gaiaAccent.base,
  '--revup-mark-surface': gaiaAccent.wash,
  '--revup-optional-size': gaiaFontSize.nano,
  '--revup-optional-weight': String(gaiaFontWeight.semibold),
  '--revup-optional-surface': gaiaNeutral.surfaceTint,
  '--revup-subtle-color': gaiaNeutral.textSubtle,
};
</script>

<template>
  <gaia-modal
    :style="styleVars"
    :show="props.show"
    width="560px"
    :mask-closable="!uploading"
    :close-on-esc="!uploading"
    @update:show="
      (value: boolean) => {
        if (!uploading) emit('update:show', value);
      }
    "
  >
    <template #header>
      <n-flex align="center" :size="11" :wrap="false">
        <div class="mark">
          <n-icon :component="IconUpload" :size="15" />
        </div>
        <n-flex vertical :size="1" :style="{ minWidth: 0 }">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Upload new version</gaia-text>
          <gaia-text truncate size="meta" tone="subtle">{{ props.fileName }}</gaia-text>
        </n-flex>
      </n-flex>
    </template>

    <n-flex vertical :size="14">
      <!-- The dropzone doubles as the picked-file summary, so choosing a file
           replaces the prompt in place rather than adding a row below it. -->
      <n-upload
        :show-file-list="false"
        :disabled="uploading || uploadDone"
        :custom-request="handleCustomRequest"
        @change="handleChange"
      >
        <n-upload-dragger class="dropzone">
          <n-flex vertical align="center" :size="8">
            <n-icon :component="IconUpload" :size="24" :color="subtleColor" />
            <template v-if="selectedFile">
              <gaia-text size="row" weight="semibold" tone="ink">{{ selectedFile.name }}</gaia-text>
              <gaia-text size="control" tone="faint">
                {{ formatBytes(selectedFile.size) }} · replaces the current version
              </gaia-text>
            </template>
            <template v-else>
              <gaia-text size="row" weight="medium" tone="ink">Click or drag a file here</gaia-text>
              <gaia-text size="control" tone="faint">
                Any file type · becomes the newest version
              </gaia-text>
            </template>
          </n-flex>
        </n-upload-dragger>
      </n-upload>

      <n-flex vertical :size="6">
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-text size="control" weight="semibold" tone="text">What changed?</gaia-text>
          <span class="optional">optional</span>
        </n-flex>
        <gaia-textarea
          v-model:value="description"
          placeholder="e.g. Updated clause 4.2, fixed signature block…"
          :disabled="uploading || uploadDone"
          :autosize="{ minRows: 2, maxRows: 5 }"
          style="width: 100%"
        />
      </n-flex>

      <n-flex v-if="uploading || uploadDone" vertical :size="7">
        <gaia-progress-bar :percentage="progress" />
        <n-flex align="center" :size="7" :wrap="false">
          <n-icon v-if="uploadDone" :component="IconCheckCircle" :size="14" :color="successColor" />
          <gaia-text size="control" :tone="uploadDone ? 'text' : 'tertiary'">
            {{ uploadDone ? 'Uploaded — opening the new version…' : `Uploading… ${progress}%` }}
          </gaia-text>
        </n-flex>
      </n-flex>

      <gaia-banner v-if="errorMsg" type="error">{{ errorMsg }}</gaia-banner>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" :disabled="uploading" @click="handleClose">Cancel</gaia-button>
        <gaia-upload-button
          subject="version"
          size="sm"
          :icon="false"
          :loading="uploading"
          :disabled="!canUpload"
          @click="handleUpload"
        />
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* The accent-tinted tile the vault's modal headers share. */
.mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--revup-mark-surface);
  color: var(--revup-accent-color);
}

/* naive's dragger draws its own dashed frame; these are the mock's metrics. */
.dropzone {
  padding: 32px 20px;
  border-radius: 11px;
}

.optional {
  display: inline-flex;
  align-items: center;
  height: 17px;
  padding: 0 7px;
  border-radius: 8px;
  background: var(--revup-optional-surface);
  font-size: var(--revup-optional-size);
  font-weight: var(--revup-optional-weight);
  color: var(--revup-subtle-color);
}
</style>
